import { RARITY } from "./constants.js";
import { ELEMENTS } from "../items/ItemFactory.js";

/**
 * Named Relics — the oldest artifacts in Relikara, each with a memory strong
 * enough to have a personality. A named relic has an AFFINITY for one hero:
 * in the right hands it grants a bonus and whispers; in the wrong hands it is
 * weaker, and the proudest of them REFUSE lesser wielders entirely.
 *
 * Named relics start already partly-awakened (they arrive with a memory), and
 * they always carry an elemental enchant. They drop rarely, and more often the
 * deeper you go / the more the Vault has come to know you (notoriety).
 */

// affinity → the hero classId that resonates with the relic
export const NAMED_RELICS = [
  {
    id: "gravewarden", name: "Gravewarden", affinity: "knight", refuseOthers: true,
    base: { wtype: "melee", dmg: [16, 24], range: 3.0, cd: 0.5, model: "weapon/sword2" },
    element: "arcane", elementPower: 1.4, rarity: 3, seedMemory: { kills: 120, depth: 3 },
    flavor: "The blade that held the western gate. It remembers every soul it failed to save.",
    whispers: ["Hold the line.", "Not one step back.", "They will not pass while I remember."],
  },
  {
    id: "worldcleaver", name: "Worldcleaver", affinity: "barbarian", refuseOthers: true,
    base: { wtype: "melee", dmg: [22, 34], range: 2.9, cd: 0.68, model: "weapon/axe2" },
    element: "shock", elementPower: 1.5, rarity: 3, seedMemory: { kills: 160 },
    flavor: "Forged from a mountain that Relikara swallowed. It has been angry ever since.",
    whispers: ["Break it.", "Break all of it.", "The stone remembers falling."],
  },
  {
    id: "whisperfang", name: "Whisperfang", affinity: "rogue", refuseOthers: false,
    base: { wtype: "melee", dmg: [10, 15], range: 2.3, cd: 0.18, model: "weapon/dagger" },
    element: "poison", elementPower: 1.3, rarity: 3, seedMemory: { kills: 90, depth: 4 },
    flavor: "A thief's blade that has never once been caught. It keeps every secret it has cut loose.",
    whispers: ["Quietly now.", "No one saw.", "One more, then we vanish."],
  },
  {
    id: "farsong", name: "Farsong", affinity: "ranger", refuseOthers: false,
    base: { wtype: "ranged", dmg: [14, 20], range: 17, cd: 0.5, model: "weapon/bow" },
    element: "frost", elementPower: 1.35, rarity: 3, seedMemory: { kills: 100, depth: 3 },
    flavor: "A bow that hears the Vault breathe. It looses true before the target knows to run.",
    whispers: ["It moves left.", "Loose now.", "The corridor lied; I did not."],
  },
  {
    id: "the-first-question", name: "The First Question", affinity: "mage", refuseOthers: true,
    base: { wtype: "ranged", dmg: [16, 24], range: 15, cd: 0.6, model: "weapon/staff", magic: true, aoe: 3.2 },
    element: "arcane", elementPower: 1.6, rarity: 3, seedMemory: { kills: 80, depth: 5 },
    flavor: "The staff of the scholar who first mapped the impossible halls. It is still asking.",
    whispers: ["Why does the door exist?", "The angle is wrong on purpose.", "Ask, and it opens."],
  },
];

const byId = Object.fromEntries(NAMED_RELICS.map((r) => [r.id, r]));
export const getRelic = (id) => byId[id] || null;

/** Build a concrete weapon item from a named-relic template. */
export function makeNamedRelic(def) {
  const el = ELEMENTS[def.element];
  const w = {
    kind: "weapon", named: true, relicId: def.id, name: def.name,
    wtype: def.base.wtype, range: def.base.range, cd: def.base.cd,
    color: el ? el.color : "#c77dff", rarity: RARITY[def.rarity] || RARITY[3],
    model: def.base.model, dmgMin: def.base.dmg[0], dmgMax: def.base.dmg[1],
    element: def.element, elementPower: def.elementPower,
    magic: !!def.base.magic, aoe: def.base.aoe || 0,
    affinity: def.affinity, refuseOthers: !!def.refuseOthers,
    whispers: def.whispers || [], flavor: def.flavor,
    memory: { kills: 0, depth: 0, saves: 0, tierIdx: 0, hero: null, ...(def.seedMemory || {}) },
  };
  return w;
}

/**
 * Roll for a named relic drop. Base odds are low and rise with depth and the
 * Vault's notoriety. Returns a relic item or null.
 */
export function rollNamedRelic(floor = 1, notoriety = 0) {
  const chance = 0.02 + floor * 0.006 + Math.min(0.12, notoriety * 0.0006);
  if (Math.random() > chance) return null;
  const def = NAMED_RELICS[Math.floor(Math.random() * NAMED_RELICS.length)];
  return makeNamedRelic(def);
}

/**
 * Resolve how a hero relates to a (named) weapon.
 *  → { wield: bool, mult: number, note: string }
 * Non-named weapons: always wieldable, no modifier.
 */
export function affinityFor(weapon, heroId, attuneAny = false) {
  if (!weapon || !weapon.named || !weapon.affinity) return { wield: true, mult: 1, note: "" };
  if (weapon.affinity === heroId) return { wield: true, mult: 1.18, note: "Attuned — the relic answers to you." };
  if (attuneAny) return { wield: true, mult: 1, note: "Attunement research lets you wield it, though it isn't yours." };
  if (weapon.refuseOthers) return { wield: false, mult: 1, note: "The relic refuses your hand. (Research Attunement to wield it.)" };
  return { wield: true, mult: 0.82, note: "Dissonant — it resists a stranger's grip." };
}
